import axios from "axios";
import { createAuthHeaders } from "../api-client";
import type { PresentationConfig } from "@/stores/slide-store";

const API_URL = import.meta.env.VITE_API_URL || "http://localhost:3001";

export interface EmailVerificationStatusResponse {
  emailVerified: boolean;
  requiresVerification: boolean;
  replyCount: number;
}

// Entry-based data model types (v7)
export type EntryType = "user" | "assistant" | "system" | "summary";
export type ContentType =
  | "text"
  | "tool_use"
  | "tool_result"
  | "image"
  | "interrupt"
  | "error";

export interface SessionEntry {
  id: string;
  sessionId: string;
  parentEntryId: string | null;
  entryType: EntryType;
  contentType: ContentType;
  entryOrder: number;
  role: string | null;
  content: string | null;
  model: string | null;
  toolUseId: string | null;
  toolName: string | null;
  toolInput: Record<string, unknown> | null;
  toolResult: unknown;
  toolIsError: boolean;
  inputTokens: number | null;
  outputTokens: number | null;
  cacheReadTokens: number | null;
  costUsd: string | null;
  metadata: Record<string, unknown> | null;
  createdAt: string;
}

export interface SessionSummary {
  sessionId: string;
  totalEntries: number;
  userEntries: number;
  assistantEntries: number;
  toolUseCount: number;
  errorCount: number;
  totalInputTokens: number;
  totalOutputTokens: number;
  totalCacheTokens: number;
  totalCostUsd: string;
}

export interface FailedTool {
  id: string;
  toolUseId: string;
  toolName: string;
  toolInput: Record<string, unknown>;
  toolResult: unknown;
  errorMessage: string | null;
  createdAt: string;
}

export interface ResumableTask {
  taskId: string;
  sessionId: string;
  status: string;
  errorCode: string | null;
  errorMessage: string | null;
  currentEntryId: string | null;
  queuedAt: string;
}

export interface ResumeTaskResponse {
  success: boolean;
  task: {
    id: string;

    status: string;
    sessionId: string;
  };
}

// Conversation API types
export interface ConversationActiveTask {
  taskId: string;
  status: string;
  userMessageId: string;
}

export interface ConversationResponse {
  messages: unknown[]; // Message type from agentChatStore
  activeTasks: ConversationActiveTask[];
}

export interface UpdateThemeResponse {
  success: boolean;
  theme: "light" | "dark";
  themeName: string;
  message: string;
}

export const claudeAgentApi = {
  /**
   * Get conversation messages and active tasks for a session
   * Used for loading session history and resuming streaming tasks
   */
  async getConversation(
    token: string,
    sessionId: string,
  ): Promise<ConversationResponse> {
    const response = await axios.get<ConversationResponse>(
      `${API_URL}/api/claude-agent/sessions/${sessionId}/conversation`,
      {
        headers: createAuthHeaders(token),
      },
    );
    return response.data;
  },

  /**
   * Check email verification status for chat gating
   * Returns whether user needs to verify email before sending more messages
   */
  async checkEmailVerificationStatus(
    token: string,
  ): Promise<EmailVerificationStatusResponse> {
    const response = await axios.get<EmailVerificationStatusResponse>(
      `${API_URL}/api/claude-agent/email-verification-status`,
      {
        headers: { Authorization: `Bearer ${token}` },
      },
    );
    return response.data;
  },

  // === Entry-based Data Model APIs (v7) ===

  /**
   * Get all entries for a session as a tree structure
   * Returns entries with parent-child relationships
   */
  async getEntryTree(
    token: string,
    sessionId: string,
  ): Promise<{ entries: SessionEntry[] }> {
    const response = await axios.get<{ entries: SessionEntry[] }>(
      `${API_URL}/api/claude-agent/sessions/${sessionId}/entries`,
      {
        headers: createAuthHeaders(token),
      },
    );
    return response.data;
  },

  /**
   * Get session summary with aggregated stats
   * Includes entry counts, token usage, and cost totals
   */
  async getSessionSummary(
    token: string,
    sessionId: string,
  ): Promise<SessionSummary> {
    const response = await axios.get<SessionSummary>(
      `${API_URL}/api/claude-agent/sessions/${sessionId}/summary`,
      {
        headers: createAuthHeaders(token),
      },
    );
    return response.data;
  },

  /**
   * Search entries within a session
   * Supports filtering by tool name, file path, and error status
   */
  async searchEntries(
    token: string,
    sessionId: string,
    filters?: {
      tool?: string;
      file?: string;
      errorOnly?: boolean;
    },
  ): Promise<{ entries: SessionEntry[] }> {
    const params = new URLSearchParams();
    if (filters?.tool) params.append("tool", filters.tool);
    if (filters?.file) params.append("file", filters.file);
    if (filters?.errorOnly) params.append("errorOnly", "true");

    const response = await axios.get<{ entries: SessionEntry[] }>(
      `${API_URL}/api/claude-agent/sessions/${sessionId}/entries/search?${params.toString()}`,
      {
        headers: createAuthHeaders(token),
      },
    );
    return response.data;
  },

  /**
   * Get failed tool executions for a session
   * Returns tool_result entries where toolIsError is true
   */
  async getFailedTools(
    token: string,
    sessionId: string,
  ): Promise<{ failedTools: FailedTool[] }> {
    const response = await axios.get<{ failedTools: FailedTool[] }>(
      `${API_URL}/api/claude-agent/sessions/${sessionId}/failed-tools`,
      {
        headers: createAuthHeaders(token),
      },
    );
    return response.data;
  },

  /**
   * Get resumable tasks for a session
   * Returns tasks in PAUSED status with isResumable=true
   */
  async getResumableTasks(
    token: string,
    sessionId: string,
  ): Promise<{ tasks: ResumableTask[] }> {
    const response = await axios.get<{ tasks: ResumableTask[] }>(
      `${API_URL}/api/claude-agent/sessions/${sessionId}/resumable-tasks`,
      {
        headers: createAuthHeaders(token),
      },
    );
    return response.data;
  },

  /**
   * Resume a paused task (for recoverable API errors like 429, 529)
   * Requeues the task for processing
   */
  async resumeTask(token: string, taskId: string): Promise<ResumeTaskResponse> {
    const response = await axios.post<ResumeTaskResponse>(
      `${API_URL}/api/claude-agent/tasks/${taskId}/resume`,
      {},
      {
        headers: createAuthHeaders(token),
      },
    );
    return response.data;
  },

  /**
   * Read a file from the dev container via sidecar proxy
   */
  async readFile(
    token: string,
    devContainerId: string,
    filePath: string,
  ): Promise<{ content: string }> {
    const response = await axios.get<{ content: string }>(
      `${API_URL}/api/claude-agent/read-file`,
      {
        params: { devContainerId, filePath },
        headers: createAuthHeaders(token),
      },
    );
    return response.data;
  },

  /**
   * Reorder slides in slides.json via sidecar
   */
  async reorderSlides(
    token: string,
    devContainerId: string,
    slideOrder: string[],
  ): Promise<{ success: boolean; slides?: unknown[] }> {
    const response = await axios.put<{ success: boolean; slides?: unknown[] }>(
      `${API_URL}/api/claude-agent/slides/reorder`,
      {
        devContainerId,
        slideOrder,
      },
      {
        headers: createAuthHeaders(token),
      },
    );
    return response.data;
  },

  /**
   * Update presentation config in slides.json via sidecar
   */
  async updateSlidesConfig(
    token: string,
    devContainerId: string,
    config: PresentationConfig,
  ): Promise<{ success: boolean; config?: PresentationConfig }> {
    const response = await axios.put<{
      success: boolean;
      config?: PresentationConfig;
    }>(
      `${API_URL}/api/claude-agent/slides/config`,
      {
        devContainerId,
        config,
      },
      {
        headers: createAuthHeaders(token),
      },
    );
    return response.data;
  },

  /**
   * Update per-slide config override in slides.json via sidecar
   */
  async updateSlideConfig(
    token: string,
    devContainerId: string,
    slideId: string,
    config: {
      interval?: number;
      transition?: string;
      transitionDuration?: number;
    },
  ): Promise<{ success: boolean }> {
    const response = await axios.put<{ success: boolean }>(
      `${API_URL}/api/claude-agent/slides/${slideId}/config`,
      {
        devContainerId,
        config,
      },
      {
        headers: createAuthHeaders(token),
      },
    );
    return response.data;
  },

  /**
   * Update IDE theme (dark/light) in the dev container
   */
  async updateTheme(
    token: string,
    devContainerId: string,
    theme: "light" | "dark",
  ): Promise<UpdateThemeResponse> {
    const response = await axios.post<UpdateThemeResponse>(
      `${API_URL}/api/claude-agent/update-theme`,
      {
        devContainerId,
        theme,
      },
      {
        headers: createAuthHeaders(token),
      },
    );
    return response.data;
  },
};
